import { sortBy, isEqual, findIndex, filter, size, cloneDeep } from 'lodash';

import { SEGMENT_TYPE } from 'itrvl-types';
import { Dinero } from 'itrvl-pricing';

import logger from 'itrvl-logger';
const log = logger(__filename);
log.trace(__filename);

// ============================================
// Helper Functions
// ============================================

/**
 * Check if a segment is DMC Own Arranged (arrangedBy === 'DMC' with no supplierCode)
 */
const isDmcOwnArranged = seg => seg.arrangedBy === 'DMC' && (!seg.supplierCode || seg.supplierCode === '');

/**
 * Check if a segment is Agent Own Arranged (arrangedBy === 'Agent')
 */
const isAgentOwnArranged = seg => seg.arrangedBy === 'Agent';

/**
 * Get a unique supplier identifier for a segment.
 * For Own Arranged segments, prepends the type to avoid ambiguity when multiple segments share the same location.
 */
const getSupplier = seg => {
  if (isDmcOwnArranged(seg)) {
    return `DMC:${seg.locationCode}`;
  }
  if (isAgentOwnArranged(seg)) {
    return `Agent:${seg.locationCode}`;
  }
  return seg.locationName || seg.locationCode;
};

/**
 * Find the Nth occurrence of a STAY segment matching a supplier in segmentsOriginal.
 * Returns { index, segment } or { index: -1, segment: null } if not found.
 */
const findNthStayBySupplier = (segmentsOriginal, supplierName, ordinal) => {
  let count = 0;
  let searchIndex = 0;

  while (searchIndex < size(segmentsOriginal) && count < ordinal) {
    const idx = findIndex(segmentsOriginal, so => so.type === SEGMENT_TYPE.STAY && getSupplier(so) === supplierName, searchIndex);

    if (idx === -1) break;

    count++;
    if (count === ordinal) {
      return { index: idx, segment: segmentsOriginal[idx] };
    }
    searchIndex = idx + 1;
  }

  return { index: -1, segment: null };
};

/**
 * Count how many times a supplier appears in segments up to (and including) targetIdx.
 */
const countSupplierOccurrences = (segments, supplierName, targetIdx) => {
  let count = 0;
  for (let i = 0; i <= targetIdx; i++) {
    if (getSupplier(segments[i]) === supplierName) {
      count++;
    }
  }
  return count;
};

/**
 * Find adjacent STAY transitions (supplier1 -> supplier2) in itinerarySegments
 * and return which occurrence the transition at targetIdx is (1st, 2nd, etc.).
 * Returns { ordinal, found } where ordinal is which occurrence this is, and found indicates if the transition exists at targetIdx.
 */
const findTransitionOrdinal = (itinerarySegments, supplier1, supplier2, targetIdx) => {
  let ordinal = 0;

  for (let i = 0; i <= targetIdx && i < size(itinerarySegments) - 1; i++) {
    if (getSupplier(itinerarySegments[i]) === supplier1 && getSupplier(itinerarySegments[i + 1]) === supplier2) {
      ordinal++;
    }
  }

  // Check if the transition actually exists at targetIdx
  const found =
    targetIdx < size(itinerarySegments) - 1 &&
    getSupplier(itinerarySegments[targetIdx]) === supplier1 &&
    getSupplier(itinerarySegments[targetIdx + 1]) === supplier2;

  return { ordinal, found };
};

/**
 * Find the Nth occurrence of a direct supplier1 -> supplier2 transition in segmentsOriginal.
 * Returns { fromIndex, toIndex } or { fromIndex: -1, toIndex: -1 } if not found.
 */
const findNthTransition = (segmentsOriginal, supplier1, supplier2, targetOrdinal) => {
  let ordinal = 0;
  let searchStart = 0;

  while (searchStart < size(segmentsOriginal)) {
    const idx1 = findIndex(segmentsOriginal, so => so.type === SEGMENT_TYPE.STAY && getSupplier(so) === supplier1, searchStart);

    if (idx1 === -1) break;

    const idx2 = findIndex(segmentsOriginal, so => so.type === SEGMENT_TYPE.STAY && getSupplier(so) === supplier2, idx1 + 1);

    const nextStay = findIndex(segmentsOriginal, { type: SEGMENT_TYPE.STAY }, idx1 + 1);

    // Check if supplier2 is the immediate next stay (not just any later stay)
    if (idx1 !== -1 && idx2 !== -1 && nextStay === idx2) {
      ordinal++;
      if (ordinal === targetOrdinal) {
        return { fromIndex: idx1, toIndex: idx2 };
      }
    }

    searchStart = idx1 + 1;
  }

  return { fromIndex: -1, toIndex: -1 };
};

/**
 * Filter activities to retain based on nights change.
 * Keeps activities that fit within new night count, adjusting departure-day activities.
 */
const filterActivitiesForNightsChange = (activities, originalNights, newNights) => {
  return activities
    .map(activity => {
      if (activity.day === originalNights + 1) {
        // Departure day activity - adjust to new departure day
        const adjusted = cloneDeep(activity);
        adjusted.day = newNights + 1;
        return adjusted;
      }
      if (activity.day <= newNights) {
        return activity;
      }
      return null;
    })
    .filter(Boolean);
};

/**
 * Remove activities that belong to a specific accommodation (by parentBySequence).
 */
const removeAccommodationActivities = (segments, accommodationSequence) => {
  return segments.filter(seg => !(seg.type === SEGMENT_TYPE.SERVICE && seg.parentBySequence === accommodationSequence));
};

// ============================================
// Segment Processors
// ============================================

const processEntryTransit = ({ segment, segmentsOriginal, retainTransit, retainActivities }) => {
  const supplier = getSupplier(segment);
  const firstStayIdx = findIndex(segmentsOriginal, { type: SEGMENT_TYPE.STAY });

  if (firstStayIdx === -1 || getSupplier(segmentsOriginal[firstStayIdx]) !== supplier) {
    return [];
  }

  if (!retainTransit) {
    return [];
  }

  // Get segments between entry (index 1) and first stay
  let transitSegments = segmentsOriginal.slice(1, firstStayIdx);

  // Remove orphaned activities (activities whose parent transit doesn't exist)
  transitSegments = transitSegments.filter(seg => {
    if (seg.type !== SEGMENT_TYPE.SERVICE) return true;
    return transitSegments.some(parent => parent.sequence === seg.parentBySequence);
  });

  if (!retainActivities) {
    transitSegments = transitSegments.filter(seg => seg.type !== SEGMENT_TYPE.SERVICE);
  }

  log.trace('entry', transitSegments);
  return transitSegments;
};

const processAccommodationRetention = ({ segment, itinerarySegments, segmentsOriginal }) => {
  const locationName = segment.locationName;
  const locationCode = segment.locationCode;

  // Find ordinal position in UI segments using appropriate matching strategy
  let sameInUI;
  let sameInOriginal;

  if (isDmcOwnArranged(segment)) {
    // DMC Own Arranged: match by arrangedBy='DMC', empty supplierCode, and locationCode
    sameInUI = filter(
      itinerarySegments,
      seg => seg.type === SEGMENT_TYPE.STAY && isDmcOwnArranged(seg) && seg.locationCode === locationCode,
    );
    sameInOriginal = filter(
      segmentsOriginal,
      seg => seg.type === SEGMENT_TYPE.STAY && isDmcOwnArranged(seg) && seg.locationCode === locationCode,
    );
  } else if (isAgentOwnArranged(segment)) {
    // Agent Own Arranged: match by arrangedBy='Agent' and locationCode
    sameInUI = filter(itinerarySegments, { type: SEGMENT_TYPE.STAY, arrangedBy: 'Agent', locationCode });
    sameInOriginal = filter(segmentsOriginal, { type: SEGMENT_TYPE.STAY, arrangedBy: 'Agent', locationCode });
  } else {
    // Regular segments: match by locationName, excluding Own Arranged segments
    sameInUI = filter(
      itinerarySegments,
      seg => seg.type === SEGMENT_TYPE.STAY && !isDmcOwnArranged(seg) && !isAgentOwnArranged(seg) && seg.locationName === locationName,
    );
    sameInOriginal = filter(
      segmentsOriginal,
      seg => seg.type === SEGMENT_TYPE.STAY && !isDmcOwnArranged(seg) && !isAgentOwnArranged(seg) && seg.locationName === locationName,
    );
  }

  const ordinal = findIndex(sameInUI, segment);
  const matchedOriginal = sameInOriginal[ordinal];

  if (!matchedOriginal) {
    return segment;
  }

  const roomChange = !isEqual(sortBy(matchedOriginal.optionKeys), sortBy(segment.optionKeys));
  if (!roomChange) {
    segment.id = matchedOriginal.id;
    segment.fkKey = matchedOriginal.fkKey;
  }

  if (matchedOriginal.sellOverride) {
    segment.sell = matchedOriginal.sell;
    segment.sellAmount = matchedOriginal.sellAmount;
    segment.sellOverride = matchedOriginal.sellOverride;
  }

  if (matchedOriginal.linked) {
    segment.linked = matchedOriginal.linked;
  }

  log.trace('segment', segment);
  return segment;
};

const processAccommodationActivities = ({ segment, idx, itinerarySegments, segmentsOriginal }) => {
  const supplier = getSupplier(segment);
  const ordinal = countSupplierOccurrences(itinerarySegments, supplier, idx);

  const { index: accommodationIdx, segment: originalAccommodation } = findNthStayBySupplier(segmentsOriginal, supplier, ordinal);

  if (accommodationIdx === -1) {
    return [];
  }

  // Get activities linked to this accommodation
  const activities = filter(segmentsOriginal, {
    type: SEGMENT_TYPE.SERVICE,
    parentBySequence: originalAccommodation.sequence,
  });

  const filtered = filterActivitiesForNightsChange(activities, originalAccommodation.nights, segment.nights);

  log.trace('activities', filtered);
  return filtered;
};

const processTransitBetweenAccommodations = ({ segment, idx, itinerarySegments, segmentsOriginal, retainActivities }) => {
  const supplier1 = getSupplier(segment);
  const supplier2 = getSupplier(itinerarySegments[idx + 1]);

  // Count which occurrence of this transition we're at
  const { ordinal, found } = findTransitionOrdinal(itinerarySegments, supplier1, supplier2, idx);

  if (!found) {
    return [];
  }

  // Find the matching transition in original segments
  const { fromIndex, toIndex } = findNthTransition(segmentsOriginal, supplier1, supplier2, ordinal);

  if (fromIndex === -1 || toIndex === -1) {
    return [];
  }

  // Get segments between the two accommodations
  let transitSegments = segmentsOriginal.slice(fromIndex + 1, toIndex);

  if (!retainActivities) {
    transitSegments = transitSegments.filter(seg => seg.type !== SEGMENT_TYPE.SERVICE);
  } else {
    // Remove activities that belong to the departure accommodation
    transitSegments = removeAccommodationActivities(transitSegments, segmentsOriginal[fromIndex].sequence);
  }

  log.trace('transit', transitSegments);
  return transitSegments;
};

const processExitTransit = ({ segment, idx, itinerarySegments, segmentsOriginal, retainTransit, retainActivities }) => {
  if (!retainTransit) {
    return [];
  }

  const supplier = getSupplier(segment);
  const ordinal = countSupplierOccurrences(itinerarySegments, supplier, idx);

  const { index: lastStayIdx, segment: originalAccommodation } = findNthStayBySupplier(segmentsOriginal, supplier, ordinal);

  if (lastStayIdx === -1) {
    return [];
  }

  // Check if this is actually the last stay in the original segments
  const nextStayIdx = findIndex(segmentsOriginal, { type: SEGMENT_TYPE.STAY }, lastStayIdx + 1);
  if (nextStayIdx !== -1) {
    // Not the last stay, so no exit transit from this accommodation
    return [];
  }

  // Get segments between last stay and exit (excluding exit itself)
  let transitSegments = segmentsOriginal.slice(lastStayIdx + 1, -1);

  // Remove DMC lines (handled separately)
  transitSegments = transitSegments.filter(seg => !seg.isPackageBookingLine);

  if (!retainActivities) {
    transitSegments = transitSegments.filter(seg => seg.type !== SEGMENT_TYPE.SERVICE);
  } else {
    // Remove activities belonging to the last accommodation (already included)
    transitSegments = removeAccommodationActivities(transitSegments, originalAccommodation.sequence);
  }

  log.trace('exit', transitSegments);
  return transitSegments;
};

const processDmcLines = segmentsOriginal => {
  return segmentsOriginal.filter(seg => seg.isPackageBookingLine);
};

const applyCurrencyConversion = (segments, itineraryOriginal) => {
  const oldCurrency = itineraryOriginal?.finance?.currency || 'USD';
  const newCurrency = 'USD';

  if (!oldCurrency || oldCurrency === newCurrency) {
    return segments;
  }

  const exchange = { [oldCurrency]: itineraryOriginal?.exchange };
  const currencyConversionMargin = 100 / (100 - itineraryOriginal.rates.currency);

  return segments.map(segment => {
    if (!segment.sellOverride) return segment;

    const converted = cloneDeep(segment);
    const sellExchange = Dinero.exchangeSync(Dinero({ unit: segment.sell, currency: oldCurrency }), newCurrency, exchange).multiply(
      currencyConversionMargin,
    );

    converted.sell = sellExchange.toUnit();
    converted.sellAmount = sellExchange.getAmount();
    return converted;
  });
};

// ============================================
// Main Function
// ============================================

export const retainDetailsPreQuote = ({
  retainDmc,
  retainTransit,
  retainActivities,
  itinerarySegments,
  segmentsOriginal,
  itineraryOriginal,
}) => {
  log.trace('retainDetailsPreQuote', { retainActivities, retainTransit, retainDmc });

  if (!retainTransit && !retainActivities && !retainDmc) {
    return undefined;
  }

  const segmentsActivitiesTransit = itinerarySegments.flatMap((segment, idx) => {
    const segmentsToPush = [];
    const isFirst = idx === 0;
    const isLast = idx === size(itinerarySegments) - 1;

    // 1. Entry transit (first segment only)
    if (isFirst) {
      segmentsToPush.push(
        ...processEntryTransit({
          segment,
          segmentsOriginal,
          retainTransit,
          retainActivities,
        }),
      );
    }

    // 2. Accommodation retention (always)
    const processedSegment = processAccommodationRetention({
      segment,
      itinerarySegments,
      segmentsOriginal,
    });
    segmentsToPush.push(processedSegment);

    // 3. Accommodation activities
    if (retainActivities) {
      segmentsToPush.push(
        ...processAccommodationActivities({
          segment,
          idx,
          itinerarySegments,
          segmentsOriginal,
        }),
      );
    }

    // 4. Transit to next accommodation
    if (retainTransit && !isLast) {
      segmentsToPush.push(
        ...processTransitBetweenAccommodations({
          segment,
          idx,
          itinerarySegments,
          segmentsOriginal,
          retainActivities,
        }),
      );
    }

    // 5. Exit transit (last segment only)
    if (isLast) {
      segmentsToPush.push(
        ...processExitTransit({
          segment,
          idx,
          itinerarySegments,
          segmentsOriginal,
          retainTransit,
          retainActivities,
        }),
      );

      // 6. DMC lines (last segment only)
      if (retainDmc) {
        segmentsToPush.push(...processDmcLines(segmentsOriginal));
      }
    }

    // 7. Currency conversion
    const converted = applyCurrencyConversion(segmentsToPush, itineraryOriginal);

    log.trace('flat', converted);
    return converted.length ? converted : segment;
  });

  const result = cloneDeep(segmentsActivitiesTransit);
  result?.forEach(segment => delete segment.currency);
  return result;
};
